import { AppIcon } from '@/components/ui/app-icon';
import { iconCoin } from '@/lib/icons';
import { cn } from '@/lib/utils';

const formats = new Map<number, Intl.NumberFormat>();

/** The amount to `maxDecimals` places; a cost above zero that would round
 *  to 0 reads "<0.01" (or "<0.1"), never as free. */
function formatCredits(amount: number, maxDecimals: number): string {
  let format = formats.get(maxDecimals);
  if (!format) {
    format = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: maxDecimals,
    });
    formats.set(maxDecimals, format);
  }
  const text = format.format(amount);
  if (amount > 0 && Number(text.replace(/,/g, '')) === 0)
    return `<${format.format(10 ** -maxDecimals)}`;
  return text;
}

/**
 * A credit cost written the way the rest of the app writes it: the coin and
 * the number, never the word. Unboxed, so it can sit inside a button, a menu
 * row or a select option (CostPill is the boxed, per-row variant).
 */
export function CreditAmount({
  amount,
  prefix,
  suffix,
  onPrimary = false,
  maxDecimals = 1,
  className,
}: {
  amount: number;
  /** Decimal places kept: 1 by default, 2 for quotes that go finer. */
  maxDecimals?: number;
  /** e.g. "~" for an estimate. */
  prefix?: string;
  /** e.g. "/ lead". */
  suffix?: string;
  /** On a filled primary button the coin is lightened to stay readable. */
  onPrimary?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-label={amount + (amount === 1 ? ' credit' : ' credits')}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 tabular-nums',
        className
      )}
    >
      <AppIcon
        icon={iconCoin}
        className={onPrimary ? 'text-emerald-200' : 'text-emerald-500'}
        style={{ fontSize: 12 }}
      />
      {prefix}
      {formatCredits(amount, maxDecimals)}
      {suffix && <span className='opacity-70'>{suffix}</span>}
    </span>
  );
}
